/**
 * Failed sessions in the console (ADR 0059): the cause and the one fix the drawer offers, the
 * 24 hour window a failure leads in, and folding failures with the same cause and source into
 * one row. Pure, so it is tested.
 */
import type { FailureCause, Id, Session, SessionFailure } from '@orgus/protocol';
import { retryModelFor, sessionFailureOf, sessionStateOf } from '@orgus/protocol';
import { modelLabel } from './labels.ts';
import { missionIdOfTitle } from './shownTitle.ts';
import { sessionOutcome } from './outcome.ts';

/** A failure leads the Sessions page for a day; after that it is history (ADR 0059 decision 3). */
export const FAILED_RECENT_MS = 24 * 3_600_000;

/** The prompt a retry or a resume continues with. */
export const CONTINUE_PROMPT = 'Continue the task above.';

/** A failed session's failure: what its agent classified, else what Orgus reads from it, else a plain one. */
export function failureOf(s: Session): SessionFailure | null {
  // A failure a later turn recovered from (a retry that finished) is not the outcome.
  if (s.status !== 'ended' || sessionOutcome(s) === 'succeeded') return null;
  const f = sessionFailureOf(s);
  if (f) return f;
  return sessionStateOf(s) === 'failed' ? { cause: 'crash', summary: 'The session failed.', at: s.lastActivity } : null;
}

/**
 * A failure as a row's meta fact (content.md, Labels): one short clause in sentence case with no
 * ending punctuation. "The model claude-haiku-5 does not exist or this account cannot use it."
 * reads "Model claude-haiku-5 unavailable". The drawer's card keeps the full sentence.
 */
export function failureLine(f: Pick<SessionFailure, 'cause' | 'summary' | 'model'>): string {
  if (f.cause === 'model-unavailable') {
    const named = f.model ?? /model (\S+) does not exist/i.exec(f.summary)?.[1];
    return named ? `Model ${named} unavailable` : 'Model unavailable';
  }
  const text = f.summary.replace(/\s+/g, ' ').trim().replace(/[.\s]+$/, '');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Failed';
}

/** Failed in the last 24 hours: it leads. A Failed group holding only older ones folds away. */
export function isRecentFailure(s: Session, now: number): boolean {
  return !!failureOf(s) && now - s.lastActivity < FAILED_RECENT_MS;
}

export type FixAction = 'retry' | 'resume' | 'terminal' | 'none';

export interface FailureFix {
  action: FixAction;
  /** The button: verb plus object. Empty for 'none'. */
  label: string;
  /** The model a retry starts with (model-unavailable). */
  model?: string;
  /** What a resume sends as the next prompt. */
  prompt?: string;
  /** One short line under the summary: what to do, or when. */
  note?: string;
  /** Why the button cannot run yet (a limit that has not reset). */
  disabled?: string;
}

export interface FixContext {
  machineName: string;
  /** Models sessions on the machine answered with (provenModels). */
  proven?: readonly string[];
  now: number;
  /** "18:00" for a reset time. */
  clock: (ts: number) => string;
}

/** The one fix per cause (ADR 0059 decision 1). Blocked has its own card (Approve and resume). */
export function fixFor(f: SessionFailure, s: Pick<Session, 'model'>, ctx: FixContext): FailureFix {
  switch (f.cause) {
    case 'model-unavailable': {
      const model = retryModelFor(f.model ?? s.model, ctx.proven);
      return { action: 'retry', label: `Retry with ${modelLabel(model)}`, model, prompt: CONTINUE_PROMPT };
    }
    case 'auth':
      return { action: 'terminal', label: 'Open terminal', note: `Run /login there to sign in on ${ctx.machineName}, then retry.` };
    case 'usage-limit': {
      const waiting = f.resetsAt !== undefined && f.resetsAt > ctx.now;
      return {
        action: 'retry',
        label: 'Retry session',
        prompt: CONTINUE_PROMPT,
        ...(f.resetsAt !== undefined ? { note: `It resets at ${ctx.clock(f.resetsAt)}.` } : {}),
        ...(waiting ? { disabled: `The limit resets at ${ctx.clock(f.resetsAt!)}` } : {}),
      };
    }
    case 'api-error':
      return { action: 'retry', label: 'Retry session', prompt: CONTINUE_PROMPT };
    case 'crash':
      return { action: 'resume', label: 'Resume session', prompt: CONTINUE_PROMPT };
    case 'task':
      return { action: 'resume', label: 'Resume with the failure', prompt: `The last run failed: ${f.summary} Fix that, then finish the task.` };
    case 'launch':
      return { action: 'none', label: '', note: 'Change the setting it names, then start it again.' };
    case 'blocked':
      return { action: 'none', label: '' };
    case 'environment':
      return { action: 'none', label: '', note: 'Orgus moved the work to another agent. If this agent keeps failing, turn it off in Capacity, Rules.' };
    case 'runtime':
      return { action: 'none', label: '', note: `Orgus starts no new work on this agent on ${ctx.machineName} until its self-test passes. Capacity, Rules shows it.` };
  }
}

// ---------------------------------------------------------------------------
// One failure, one row (ADR 0059 decision 2)
// ---------------------------------------------------------------------------

/** Where a run came from: its mission, else its launch (the same first prompt in the same folder on the same machine). */
export function failureSourceKey(s: Pick<Session, 'title' | 'machineId' | 'cwd'>): string {
  const mission = missionIdOfTitle(s.title);
  return mission ? `mission:${mission}` : `launch:${s.machineId}|${(s.cwd ?? '').toLowerCase()}|${(s.title ?? '').trim()}`;
}

export interface FailureFold {
  /** The newest run: the row opens it. */
  head: Session;
  /** Every run in the fold, newest first (head included). */
  runs: Session[];
  cause: FailureCause;
  /** The cause as a row's meta fact (failureLine). */
  summary: string;
  missionId: Id | null;
}

/** Fold failed sessions with the same cause and source; a fold of one is a plain row. Newest fold first. */
export function foldFailures(list: readonly Session[]): FailureFold[] {
  const by = new Map<string, FailureFold>();
  for (const s of [...list].sort((a, b) => b.lastActivity - a.lastActivity)) {
    const f = failureOf(s);
    if (!f) continue;
    const key = `${f.cause}|${f.cause === 'model-unavailable' ? (f.model ?? '') : ''}|${failureSourceKey(s)}`;
    const fold = by.get(key);
    if (fold) fold.runs.push(s);
    else by.set(key, { head: s, runs: [s], cause: f.cause, summary: failureLine(f), missionId: missionIdOfTitle(s.title) });
  }
  return [...by.values()];
}

/** "2 runs of Fix add() failed: Model claude-haiku-5 unavailable". The part after the colon is the row's meta fact, so it keeps its capital. */
export function foldTitle(fold: Pick<FailureFold, 'runs' | 'summary'>, title: string): string {
  return `${fold.runs.length} runs of ${title} failed: ${failureLine({ cause: 'task', summary: fold.summary })}`;
}
